import {useRef, useState} from "react";
import {CardElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useQueryClient} from "@tanstack/react-query";
import moment from "moment";
import {addUserPaymentCard} from "api";
import {completeBooking, createBooking} from "api/booking.api";

// Request body for POST booking/create-booking (same fields as before).
export const buildBookingPayload = ({timing, lessonsCost, classId, typeOfClass, numberOfStudents, teacherId, onlineType}) => ({
    booking_slot: timing.map(({cost, date, time}) => ({
        class_amount: Number(String(cost).replace("$", "")),
        class_date: moment(date).format("YYYY-MM-DD"),
        class_time: time,
    })),
    booking_total_amount: lessonsCost.classCost,
    class_id: classId,
    class_type: String(typeOfClass?.value || "").toLowerCase(),
    discount_amount: lessonsCost.discount,
    no_of_class: timing.length,
    student_per_class: numberOfStudents?.value,
    tax: lessonsCost.tax,
    teacher_id: teacherId,
    total_amount: lessonsCost.totalCost,
    online_type: onlineType,
    // This client finishes 3-D Secure (handleNextAction + complete-booking).
    supports_3ds: true,
});

/** User-facing message for any error thrown by useBookingRequest. */
export const bookingErrorMessage = (error, fallback = "We couldn't send your booking request. Please try again.") =>
    error?.response?.data?.Message || error?.response?.data?.message || error?.message || fallback;

/**
 * The booking request with the existing payment rules: the card hold is
 * placed by create-booking; cards that need the bank's 3-D Secure check are
 * confirmed with Stripe and then completed. Only one request runs at a time.
 * Must be used inside Stripe <Elements>.
 */
export const useBookingRequest = () => {
    const stripe = useStripe();
    const elements = useElements();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const busyRef = useRef(false);

    // Card entered in <CardElement> -> Stripe payment method id. Throws with a readable message.
    const createCardPaymentMethod = async () => {
        if (!stripe || !elements) throw new Error("The payment form is still loading. Try again in a moment.");
        const card = elements.getElement(CardElement);
        if (!card) throw new Error("Enter your card details.");
        const result = await stripe.createPaymentMethod({type: "card", card});
        if (result.error || !result.paymentMethod) {
            throw new Error(result.error?.message || "Check your card details and try again.");
        }
        return result.paymentMethod.id;
    };

    /**
     * Sends the booking request. cardId is a saved card, or a new payment
     * method (isNewCard) that is first saved to the student's account.
     * Returns the booking; throws on failure.
     */
    const requestBooking = async ({payload, cardId, isNewCard}) => {
        if (busyRef.current) return null;
        busyRef.current = true;
        setIsSubmitting(true);
        try {
            if (isNewCard) {
                await addUserPaymentCard({PaymentMethodId: cardId});
                queryClient.invalidateQueries({queryKey: ["getUserPaymentCard"]});
            }
            const res = await createBooking({...payload, stripe_card_id: cardId});
            const booking = res?.data;
            if (booking?.requires_3ds && booking?.client_secret) {
                const {error} = await stripe.handleNextAction({clientSecret: booking.client_secret});
                if (error) {
                    throw new Error(error.message || "Your bank didn't confirm the card. Try again or use another card.");
                }
                await completeBooking(booking.id);
            }
            queryClient.invalidateQueries({queryKey: ["getAvailableDaysByMonth"]});
            queryClient.invalidateQueries({queryKey: ["getPendingClassesForUser"]});
            queryClient.invalidateQueries({queryKey: ["getUserPendingMethods"]});
            return booking;
        } finally {
            busyRef.current = false;
            setIsSubmitting(false);
        }
    };

    return {createCardPaymentMethod, requestBooking, isSubmitting, stripeReady: Boolean(stripe && elements)};
};
